import {
  BoolEquals,
  Comparison,
  DataType,
  IsOneOfInStringListFold,
  Not,
  NumberEquals,
  NumberGreaterThan,
  OrderActionEquals,
  Rule,
  StringContainsFold,
  StringExists,
  StringListContainsAll,
  StringListContainsFold,
} from '@vendasta/automata';
import { DataFilter } from '../interface';
import { BoolOptionsComponent } from './bool-options/bool-options.component';
import { BoolDisplayService } from './bool-options/bool-options.service';
import { FreeTextDisplayService } from './free-text-input/free-text-display.service';
import { FreeTextInputComponent } from './free-text-input/free-text-input.component';
import { OrderActionTypeOptionsComponent } from './order-action-type-options/order-action-type-options-component';
import { OrderActionTypeOptionsDisplayService } from './order-action-type-options/order-action-type-options-display.service';
import { FreeTextListInputComponent } from './free-text-list-input';
import { TagOptionsDisplayService } from './tag-options';
import { EmptyStringComponent, EmptyStringDisplayService } from './empty-string-component';
import { PositiveIntegerComponent, PositiveIntegerDisplayService } from './positive-integer';

// isValidParameterDataType calculates if a certain DataType is one we support
// i.e., Unknown and Unspecified are not used
export function isValidParameterDataType(dataType: DataType): boolean {
  return dataType !== DataType.DATA_TYPE_UNSPECIFIED && dataType !== DataType.DATA_TYPE_UNKNOWN;
}

// validDelayUntilDataTypes returns DataTypes we can support in the delay until
// to filter down to triggers. This is a purely a UX thing because if we show a string and the user selects it
// there won't be any triggers to select. This should eventually be removed and be replaced with better instructions
// for the user.
export const validDelayUntilDataTypes: DataType[] = [
  DataType.DATA_TYPE_ORDER_ID,
  DataType.DATA_TYPE_SMB_USER_ID,
  DataType.DATA_TYPE_ACCOUNT_GROUP_ID,
  DataType.DATA_TYPE_FULFILLMENT_PROJECT_ID,
  DataType.DATA_TYPE_EMAIL_ID,
  DataType.DATA_TYPE_CONTACT_ID,
  DataType.DATA_TYPE_COMPANY_ID,
  DataType.DATA_TYPE_TASK,
];

interface FilterDataTypeMetaData {
  dataFilter: DataFilter;
  // The rules are stored in the DataFilter, but they can't be built statically because you need the variablePath when buiding the rules so this
  // initializer needs to be run and have the returned rules assigned ot the data filter before it can be used.
  dataFilterRulesInitializer: (variablePath: string) => Rule[];
  dataType: DataType;
  repeated?: boolean;
}

const filterDataTypeMetaData: FilterDataTypeMetaData[] = [
  {
    dataFilter: {
      dataEntryComponent: BoolOptionsComponent,
      displayService: BoolDisplayService,
      rules: [],
      comparisonLabels: ['AUTOMATIONS.EDITOR.FILTERS.WORKFLOW_DATA.IS'],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [new BoolEquals(variablePath)],
    dataType: DataType.DATA_TYPE_BOOLEAN,
  },
  {
    dataFilter: {
      dataEntryComponentList: [
        FreeTextInputComponent,
        FreeTextInputComponent,
        EmptyStringComponent,
        EmptyStringComponent,
        FreeTextListInputComponent,
        FreeTextListInputComponent,
      ],
      displayService: FreeTextDisplayService,
      rules: [],
      comparisonLabels: [
        'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.CONTAINS',
        'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_CONTAIN',
        'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.VALUE_EXISTS',
        'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.VALUE_NOT_EXISTS',
        'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_ONE_OF',
        'AUTOMATIONS.EDITOR.FILTERS.PARTNER_DATA.COMPARISON.IS_NOT_ONE_OF',
      ],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [
      new StringContainsFold(variablePath),
      new Not(new StringContainsFold(variablePath)),
      new StringExists(variablePath),
      new Not(new StringExists(variablePath)),
      new IsOneOfInStringListFold(variablePath),
      new Not(new IsOneOfInStringListFold(variablePath)),
    ],
    dataType: DataType.DATA_TYPE_STRING,
  },
  {
    dataFilter: {
      dataEntryComponent: OrderActionTypeOptionsComponent,
      displayService: OrderActionTypeOptionsDisplayService,
      rules: [],
      comparisonLabels: ['AUTOMATIONS.EDITOR.FILTERS.WORKFLOW_DATA.IS'],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [new OrderActionEquals(variablePath)],
    dataType: DataType.DATA_TYPE_ORDER_ACTION_TYPE,
  },
  {
    dataFilter: {
      dataEntryComponent: FreeTextListInputComponent,
      displayService: TagOptionsDisplayService,
      rules: [],
      comparisonLabels: [
        'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ANY',
        'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.INCLUDES_ALL',
        'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
        'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
      ],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [
      new StringListContainsFold(variablePath),
      new StringListContainsAll(variablePath),
      new Not(new StringListContainsFold(variablePath)),
      new Not(new StringListContainsAll(variablePath)),
    ],
    dataType: DataType.DATA_TYPE_STRING,
    repeated: true,
  },
  {
    dataFilter: {
      dataEntryComponentList: [EmptyStringComponent, EmptyStringComponent],
      displayService: EmptyStringDisplayService,
      rules: [],
      comparisonLabels: [
        'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.VALUE_EXISTS',
        'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.VALUE_NOT_EXISTS',
      ],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [
      new StringExists(variablePath),
      new Not(new StringExists(variablePath)),
    ],
    dataType: DataType.DATA_TYPE_INVOICE,
  },
  {
    dataFilter: {
      dataEntryComponentList: [EmptyStringComponent, EmptyStringComponent],
      displayService: EmptyStringDisplayService,
      rules: [],
      comparisonLabels: [
        'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.VALUE_EXISTS',
        'AUTOMATIONS.EDITOR.FILTERS.COMPARISON.VALUE_NOT_EXISTS',
      ],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [
      new StringExists(variablePath),
      new Not(new StringExists(variablePath)),
    ],
    dataType: DataType.DATA_TYPE_ORDER_ID,
  },
  {
    dataFilter: {
      dataEntryComponentList: [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent],
      displayService: PositiveIntegerDisplayService,
      rules: [],
      comparisonLabels: [
        'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
        'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.EQUALS',
        'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
      ],
      label: '',
    },
    dataFilterRulesInitializer: (variablePath: string) => [
      new Not(new NumberGreaterThan(variablePath)),
      new NumberEquals(variablePath),
      new NumberGreaterThan(variablePath),
    ],
    dataType: DataType.DATA_TYPE_NUMBER,
  },
];

// getTypeMetaData returns a DataFilter for the given data type. This is to be used to build the form for the wanted filter
export function getTypeMetaData(dataType: DataType, key: string, repeated: boolean): DataFilter {
  const metaData = filterDataTypeMetaData.find((metaData) => {
    return metaData.dataType === dataType && !!metaData.repeated === !!repeated;
  });
  if (!metaData) {
    return undefined;
  }
  metaData.dataFilter.rules = metaData.dataFilterRulesInitializer(key);
  return metaData.dataFilter;
}

// getDataTypeFromRule takes in one of the rules from the DataFilter rules of the filterDataTypeMetaData and matches it to
// the correct DataType. Returns undefined if it can't match a rule comparison.
export function getDataTypeFromRule(rule: Rule): { dataType: DataType; repeated: boolean; comparisonIndex: number } {
  let comparisonIndex = 0;
  const found = filterDataTypeMetaData.find((metaData) => {
    const rules = metaData.dataFilterRulesInitializer('anything');
    return rules.find((r, index) => {
      if (rule.comparison === Comparison.Not && r.comparison === Comparison.Not) {
        const rNotRule: Rule = r.value as Rule;
        const ruleNotRule: Rule = rule.value as Rule;
        if (rNotRule.comparison === ruleNotRule.comparison) {
          comparisonIndex = index;
          return true;
        }
        return false;
      } else {
        if (r.comparison === rule.comparison) {
          comparisonIndex = index;
          return true;
        }
        return false;
      }
    });
  });
  if (found) {
    return { dataType: found.dataType, repeated: found.repeated, comparisonIndex: comparisonIndex };
  }
  return undefined;
}
